import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Flex,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Icon,
  Badge,
  HStack,
  VStack,
  Progress,
  Avatar,
  AvatarGroup,
  StatHelpText,
  Tooltip
} from '@chakra-ui/react';
import { SectionLabel } from './DashboardWrapper';
import AnimatedBox from '../AnimatedBox';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  ReferenceLine,
  AreaChart,
  Area,
  LineChart,
  Line
} from 'recharts';
import {
  FaChartLine,
  FaStar,
  FaUsers,
  FaMapMarkerAlt,
  FaTrophy,
  FaAward,
  FaMedal
} from 'react-icons/fa';
import RankingListComponent from '../RankingListComponent';
import ModernDetailsTable, { TrendCell, NumberCell } from '../ModernDetailsTable';
import BusinessReviewModal from '../BusinessReviewModal';
import MetricCard from '../MetricCard';
import BusinessBubbleChart from '../BusinessBubbleChart';
import { getYearData } from '../../services/yearService';
import { getBusinessImages } from '../../services/imageService';
import { useUser } from '../../contexts/UserContext';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import { getTrafficValue, sortBusinessesBySeoScore } from '../../utils/competitiveIntelHelpers';
import moment from 'moment';

const TotalReviewsDashboard = ({ reviewsData, dateRange, demoMode, demoData }) => {
  // Set up color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  const [yearData, setYearData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [combinedData, setCombinedData] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [businessImages, setBusinessImages] = useState({});

  // Colors for charts - using consistent color palette with more distinct colors
  const colors = useMemo(
    () => [
      '#2F80ED', // bright blue
      '#27AE60', // emerald green
      '#EB5757', // bright red
      '#9B51E0', // purple
      '#F2994A', // orange
      '#219EBC', // teal
      '#F264A6', // pink
      '#BB6BD9', // violet
      '#2D9CDB', // light blue
      '#219653', // dark green
      '#F2C94C', // yellow
      '#6FCF97', // light green
      '#56CCF2', // sky blue
      '#F2994A', // peach
      '#9B51E0', // lavender
      '#EB5757', // coral
      '#2F80ED', // royal blue
      '#27AE60', // forest green
      '#BB6BD9', // orchid
      '#F264A6' // rose
    ],
    []
  );
  const [businessColors, setBusinessColors] = useState({});

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          {/* Shadow layer for depth */}
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          {/* Base dark gold layer */}
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          {/* Bright gold overlay */}
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          {/* Top highlight for 3D shine */}
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Custom label renderer for pie charts that shows spinning stars for favorited businesses
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  // For Total Reviews dashboard, we show all businesses without date filtering
  // The timestamp field is when data was collected, not when reviews were made
  // Historical trends are handled separately via yearData and monthlyReviewCounts
  const filteredData = useMemo(() => {
    // Return all review data - no filtering by collection timestamp
    return reviewsData || [];
  }, [reviewsData]);

  // Sort businesses by review count
  const sortedByTotal = useMemo(() => {
    if (!filteredData.length) return [];
    return [...filteredData].sort((a, b) => b.rating_count - a.rating_count);
  }, [filteredData]);

  // Get top 5 businesses by review count
  const top5Total = useMemo(() => {
    if (!sortedByTotal.length) return [];
    return sortedByTotal.slice(0, 5);
  }, [sortedByTotal]);

  const businessSentimentData = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData.map((business) => {
      const sentimentBreakdown = business.historicalData
        ?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0
          ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) /
              total) *
            100
          : 0;
      return { ...business, sentimentScore, ...sentimentBreakdown, total };
    });
  }, [combinedData]);

  // Sort businesses based on sentiment score
  const sortedBySentiment = useMemo(() => {
    if (!businessSentimentData.length) return [];
    return [...businessSentimentData].sort(
      (a, b) => b.sentimentScore - a.sentimentScore
    );
  }, [businessSentimentData]);

  // Calculate growth metrics for each business
  const businessGrowthData = useMemo(() => {
    if (!combinedData || combinedData.length === 0) {
      return [];
    }
    return combinedData.map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      let months = Object.keys(monthlyData).sort();

      if (dateRange?.startDate && dateRange?.endDate) {
        months = months.filter((month) => {
          const monthDate = new Date(month + '-01T00:00:00');
          const startDate = new Date(dateRange.startDate);
          const endDate = new Date(dateRange.endDate);
          return monthDate >= startDate && monthDate <= endDate;
        });
      }

      if (months.length === 0) {
        return { ...business, threeMonthGrowth: 0 };
      }
      const lastThreeMonths = months.length >= 3 ? months.slice(-3) : months;
      const threeMonthGrowth = lastThreeMonths.reduce(
        (sum, month) => sum + monthlyData[month],
        0
      );
      return { ...business, threeMonthGrowth };
    });
  }, [combinedData, dateRange]);

  // Sort businesses by growth
  const sortedByGrowth = useMemo(() => {
    if (!businessGrowthData.length) return [];
    console.log(businessGrowthData);
    return [...businessGrowthData].sort(
      (a, b) => b.threeMonthGrowth - a.threeMonthGrowth
    );
  }, [businessGrowthData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(combinedData),
    [combinedData]
  );

  // Prepare data for monthly trend chart
  const monthlyTrendData = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate || !combinedData.length)
      return [];

    const monthlyData = {};

    // Initialize months in range
    const start = moment(dateRange.startDate);
    const end = moment(dateRange.endDate);
    let current = start.clone();

    while (current.isSameOrBefore(end, 'month')) {
      monthlyData[current.format('YYYY-MM')] = {
        month: current.format('YYYY-MM'),
        'Market Average': 0,
        ...top5Total.reduce(
          (acc, business) => ({
            ...acc,
            [business.name.split(' ').slice(0, 2).join(' ')]: 0
          }),
          {}
        )
      };
      current.add(1, 'month');
    }

    // Aggregate review counts by month using combinedData
    combinedData.forEach((business) => {
      if (business.historicalData?.monthlyReviewCounts) {
        Object.entries(business.historicalData.monthlyReviewCounts)
          .filter(([month]) => {
            return moment(month).isBetween(
              dateRange.startDate,
              dateRange.endDate,
              'month',
              '[]'
            );
          })
          .forEach(([month, count]) => {
            if (!monthlyData[month]) return;

            monthlyData[month]['Market Average'] += count;

            // Add individual business data for top 5
            const businessName = business.name.split(' ').slice(0, 2).join(' ');
            if (
              top5Total.some(
                (b) => b.name.split(' ').slice(0, 2).join(' ') === businessName
              )
            ) {
              monthlyData[month][businessName] = count;
            }
          });
      }
    });

    // Calculate market average
    Object.values(monthlyData).forEach((data) => {
      data['Market Average'] = Math.round(
        data['Market Average'] / combinedData.length
      );
    });

    // Sort by date
    return Object.values(monthlyData).sort((a, b) =>
      moment(a.month).diff(moment(b.month))
    );
  }, [combinedData, dateRange, top5Total]);

  // Prepare data for all businesses line chart (Cumulative Total Reviews Over Time)
  const allBusinessesLineData = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate || !combinedData.length)
      return [];

    // Get all unique months from all businesses within the date range
    const allMonths = new Set();
    combinedData.forEach((business) => {
      if (business.historicalData?.monthlyReviewCounts) {
        Object.keys(business.historicalData.monthlyReviewCounts).forEach((month) => {
          if (moment(month).isBetween(dateRange.startDate, dateRange.endDate, 'month', '[]')) {
            allMonths.add(month);
          }
        });
      }
    });

    // Sort months chronologically
    const sortedMonths = Array.from(allMonths).sort();
    if (sortedMonths.length === 0) return [];

    // Calculate starting total for each business
    // Starting total = current rating_count - reviews gained AFTER the first month in range
    const startingTotals = {};
    combinedData.forEach((business) => {
      const shortName = business.name.split(' ').slice(0, 2).join(' ');
      const currentTotal = business.rating_count || 0;
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      
      // Sum reviews from the first month onwards (these will be added cumulatively)
      let reviewsInRange = 0;
      sortedMonths.forEach((month) => {
        reviewsInRange += monthlyData[month] || 0;
      });
      
      // Starting total is current total minus all reviews in the range
      // Then we'll add them back month by month
      startingTotals[shortName] = Math.max(0, currentTotal - reviewsInRange);
    });

    // Track running totals for each business (starts at their beginning total)
    const runningTotals = { ...startingTotals };

    // Create data points for each month with CUMULATIVE total reviews
    const lineData = sortedMonths.map((month) => {
      const dataPoint = {
        month,
        displayMonth: moment(month).format('MMM YY')
      };

      // Add each business's cumulative total review count at this month
      combinedData.forEach((business) => {
        const monthlyData = business.historicalData?.monthlyReviewCounts || {};
        const shortName = business.name.split(' ').slice(0, 2).join(' ');
        // Add this month's new reviews to the running total
        runningTotals[shortName] += monthlyData[month] || 0;
        dataPoint[shortName] = runningTotals[shortName];
      });

      return dataPoint;
    });

    return lineData;
  }, [combinedData, dateRange]);

  // Get business names for the line chart legend
  const allBusinessNames = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData
      .sort((a, b) => b.rating_count - a.rating_count)
      .map((business) => business.name.split(' ').slice(0, 2).join(' '));
  }, [combinedData]);

  // Add histogram utility function
  const createHistogramBuckets = useCallback(
    (data, valueKey, bucketCount = 10) => {
      if (!data || data.length === 0) return [];

      const values = data
        .map((item) => item[valueKey])
        .filter((val) => val !== undefined && val !== null);
      if (values.length === 0) return [];

      const min = Math.min(...values);
      const max = Math.max(...values);

      // Handle the case where all values are identical
      if (max === min) {
        return [
          {
            range: `${Math.round(min)}-${Math.round(max)}`,
            min: min,
            max: max,
            count: values.length,
            businesses: [...data]
          }
        ];
      }

      const bucketSize = (max - min) / bucketCount;

      const buckets = Array.from({ length: bucketCount }, (_, i) => ({
        range: `${Math.round(min + i * bucketSize)}-${Math.round(
          min + (i + 1) * bucketSize
        )}`,
        min: min + i * bucketSize,
        max: min + (i + 1) * bucketSize,
        count: 0,
        businesses: []
      }));

      // Fill buckets
      data.forEach((item) => {
        const value = item[valueKey];
        if (value !== undefined && value !== null) {
          const bucketIndex = Math.min(
            Math.floor((value - min) / bucketSize),
            bucketCount - 1
          );
          buckets[bucketIndex].count++;
          buckets[bucketIndex].businesses.push(item);
        }
      });

      return buckets;
    },
    []
  );

  // Create histogram data for review counts
  const reviewCountHistogram = useMemo(() => {
    return createHistogramBuckets(filteredData, 'rating_count', 16);
  }, [filteredData, createHistogramBuckets]);

  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      setIsLoading(false);
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);

        // Combine data using maps_uri as unique identifier
        const combined = reviewsData.map((currentData) => {
          const historicalData = yearDataResponse.find(
            (yd) => yd.maps_uri === currentData.maps_uri
          );

          return {
            ...currentData,
            historicalData: historicalData?.data || {
              monthlyReviewCounts: {},
              monthlyRatings: {},
              sentimentBreakdown: {
                positive: 0,
                neutral: 0,
                negative: 0
              }
            }
          };
        });

        setCombinedData(combined);
      } catch (error) {
        console.error('Error fetching year data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (reviewsData?.length > 0) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [reviewsData, dateRange]);

  useEffect(() => {
    // Assign colors to businesses
    if (reviewsData) {
      const newBusinessColors = {};
      reviewsData.forEach((business, index) => {
        newBusinessColors[business.name] = colors[index % colors.length];
      });
      setBusinessColors(newBusinessColors);
    }
  }, [reviewsData, colors]);

  // Fetch business images for podium
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        // Don't block UI on image fetch failure
        console.error('Error fetching business images:', error);
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  const getColor = useCallback(
    (businessName) => {
      return businessColors[businessName] || colors[0];
    },
    [businessColors, colors]
  );

  if (isLoading) {
    return (
      <Flex
        justify='center'
        align='center'
        minH='300px'
        direction='column'
      >
        <AnimatedBox
          animation='fadeIn'
          duration={0.8}
          display='flex'
          flexDirection='column'
          alignItems='center'
        >
          <Spinner
            thickness='4px'
            speed='0.65s'
            emptyColor='gray.200'
            color='blue.500'
            size='xl'
          />
          <AnimatedBox
            animation='pulse'
            delay={0.3}
            duration={1.5}
            repeat={true}
          >
            <Text
              mt={4}
              fontSize='lg'
              color='gray.500'
            >
              Loading review data...
            </Text>
          </AnimatedBox>
        </AnimatedBox>
      </Flex>
    );
  }

  // Show empty state if no data available after filtering
  if (!reviewsData || reviewsData.length === 0 || sortedByTotal.length === 0) {
    return (
      <Flex
        justify='center'
        align='center'
        minH='300px'
        direction='column'
      >
        <AnimatedBox
          animation='fadeIn'
          duration={0.8}
          display='flex'
          flexDirection='column'
          alignItems='center'
        >
          <Text
            fontSize='lg'
            color='gray.500'
          >
            No review data available for the selected time period.
          </Text>
          <Text
            mt={2}
            fontSize='md'
            color='gray.400'
          >
            Try adjusting the date range or check back later.
          </Text>
        </AnimatedBox>
      </Flex>
    );
  }

  // Calculate market-wide metrics
  const totalMarketReviews = sortedByTotal.reduce(
    (sum, b) => sum + b.rating_count,
    0
  );
  const avgReviewsPerBusiness = totalMarketReviews / sortedByTotal.length;
  const medianReviews =
    sortedByTotal[Math.floor(sortedByTotal.length / 2)].rating_count;

  // Prepare data for pie chart
  const pieChartData = sortedByTotal.slice(0, 20).map((business) => ({
    name: business.name.split(' ').slice(0, 2).join(' '),
    value: business.rating_count,
    fill: getColor(business.name)
  }));

  // Add this before the return statement, after the monthlyTrendData useMemo
  const columns = [
    {
      id: 1,
      name: 'Business Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '200px',
      cell: (row) => (
        <Text fontWeight="semibold" fontSize="md" noOfLines={1}>{row.name}</Text>
      )
    },
    {
      id: 2,
      name: 'Total Reviews',
      selector: (row) => row.rating_count,
      sortable: true,
      right: true,
      cell: (row) => (
        <HStack spacing={2} justify="flex-end">
          <Text fontWeight="semibold" fontFeatureSettings="'tnum' 1">
            {row.rating_count?.toLocaleString()}
          </Text>
          {row.rating_count > avgReviewsPerBusiness && (
            <Badge colorScheme="green" size="sm" borderRadius="full">
              Above Avg
            </Badge>
          )}
        </HStack>
      )
    },
    {
      id: 3,
      name: 'Average Rating',
      selector: (row) => row.rating_avg,
      sortable: true,
      right: true,
      cell: (row) => {
        const rating = row.rating_avg;
        const colorScheme = rating >= 4.5 ? 'green' : rating >= 4.0 ? 'blue' : rating >= 3.5 ? 'yellow' : 'red';
        return (
          <HStack spacing={2} justify="flex-end">
            <Icon as={FaStar} color="yellow.400" boxSize={3} />
            <Badge colorScheme={colorScheme} borderRadius="full" px={2}>
              {rating?.toFixed(1) || 'N/A'}
            </Badge>
          </HStack>
        );
      }
    }
  ];

  const handleBusinessClick = (business) => {
    const businessData = combinedData.find(
      (b) => b.maps_uri === business.maps_uri
    );
    if (businessData) {
      setSelectedBusiness(businessData);
      setIsModalOpen(true);
    }
  };

  // Function to handle row clicks in data table
  const handleRowClick = (row) => {
    handleBusinessClick(row);
  };

  // Business Review Card component
  const BusinessReviewCard = ({ business, index = 0 }) => {
    // Define hover background color at component level
    const hoverBg = useColorModeValue('gray.50', 'gray.700');

    // Get color based on rating - higher is better
    const ratingColor =
      business.rating_avg > 4.5
        ? 'green.500'
        : business.rating_avg > 4.0
        ? 'blue.500'
        : business.rating_avg > 3.5
        ? 'orange.500'
        : 'gray.500';

    // Process monthly data for mini chart if available
    const monthlyData = useMemo(() => {
      if (!business.historicalData?.monthlyReviewCounts) return [];

      return Object.entries(business.historicalData.monthlyReviewCounts)
        .map(([month, count]) => ({
          month: month,
          count: count
        }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6); // Get last 6 months
    }, [business.historicalData]);

    return (
      <AnimatedBox
        animation='slideUp'
        delay={0.05 * (index % 8)} // Stagger but reset after 8 cards to avoid long delays
        duration={0.4}
        bg={cardBg}
        borderWidth='1px'
        borderColor={borderColor}
        borderRadius='lg'
        overflow='hidden'
        p={4}
        cursor='pointer'
        onClick={() => handleBusinessClick(business)}
        transition='all 0.2s'
        _hover={{
          transform: 'translateY(-4px)',
          shadow: 'lg',
          borderColor: 'blue.400',
          bg: hoverBg
        }}
        height='100%'
      >
        <VStack
          align='stretch'
          spacing={4}
          height='100%'
        >
          <Box flex='1'>
            <Heading
              size='md'
              mb={2}
              lineHeight='1.4'
              minHeight='2.8em'
              overflow='hidden'
              css={{
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical'
              }}
              title={business.name}
            >
              {business.name}
            </Heading>
            <HStack
              spacing={2}
              mb={2}
              justify='space-between'
            >
              <Badge colorScheme='blue'>
                {business.primary_type
                  ?.replace(/_/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Business'}
              </Badge>
              <HStack
                spacing={1}
                align='center'
              >
                <Icon
                  as={FaStar}
                  color='yellow.400'
                  boxSize={4}
                />
                <Text
                  as='span'
                  fontWeight='bold'
                  lineHeight='1'
                >
                  {business.rating_avg?.toFixed(1) || '0.0'}
                </Text>
                <Text
                  as='span'
                  color='gray.500'
                  lineHeight='1'
                >
                  ({parseInt(business.rating_count || 0).toLocaleString()})
                </Text>
              </HStack>
            </HStack>
          </Box>

          {/* Review Metrics */}
          <Box>
            <Text
              fontSize='sm'
              fontWeight='medium'
              mb={1}
            >
              Review Metrics
            </Text>

            {/* Mini Area Chart */}
            <Box
              h='80px'
              mb={2}
            >
              <ResponsiveContainer
                width='100%'
                height='100%'
              >
                <AreaChart data={monthlyData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient
                      id={`colorReviews-${business.name.replace(/\s+/g, '')}`}
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='1'
                    >
                      <stop
                        offset='0%'
                        stopColor={ratingColor}
                        stopOpacity={0.4}
                      />
                      <stop
                        offset='50%'
                        stopColor={ratingColor}
                        stopOpacity={0.2}
                      />
                      <stop
                        offset='100%'
                        stopColor={ratingColor}
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    <filter id={`glow-${business.name.replace(/\s+/g, '')}`} x='-20%' y='-20%' width='140%' height='140%'>
                      <feGaussianBlur stdDeviation='2' result='coloredBlur'/>
                      <feMerge>
                        <feMergeNode in='coloredBlur'/>
                        <feMergeNode in='SourceGraphic'/>
                      </feMerge>
                    </filter>
                  </defs>
                  <Area
                    type='natural'
                    dataKey='count'
                    stroke={ratingColor}
                    strokeWidth={2.5}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    fillOpacity={1}
                    fill={`url(#colorReviews-${business.name.replace(
                      /\s+/g,
                      ''
                    )})`}
                    filter={`url(#glow-${business.name.replace(/\s+/g, '')})`}
                    isAnimationActive={true}
                    animationDuration={1200}
                    animationEasing='ease-out'
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            <SimpleGrid
              columns={2}
              spacing={2}
              mb={3}
            >
              <Stat
                bg={statBg}
                p={2}
                borderRadius='md'
              >
                <StatLabel fontSize='xs'>Total Reviews</StatLabel>
                <StatNumber
                  fontSize='lg'
                  color={ratingColor}
                >
                  {business.rating_count.toLocaleString()}
                </StatNumber>
              </Stat>
              <Stat
                bg={statBg}
                p={2}
                borderRadius='md'
              >
                <StatLabel fontSize='xs'>Rating</StatLabel>
                <StatNumber
                  fontSize='lg'
                  color='blue.500'
                >
                  {business.rating_avg?.toFixed(1) || '0.0'}
                </StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>

          {/* Basic Info */}
          <VStack
            align='start'
            spacing={1}
            mt='auto'
            fontSize='sm'
          >
            <HStack
              spacing={2}
              align='center'
            >
              <Icon
                as={FaMapMarkerAlt}
                color='gray.500'
                boxSize={4}
              />
              <Text
                as='span'
                noOfLines={1}
                mb={0}
              >
                {business.address || 'No address available'}
              </Text>
            </HStack>

            <HStack
              spacing={2}
              align='center'
            >
              <Icon
                as={FaUsers}
                color='gray.500'
                boxSize={4}
              />
              <Text
                as='span'
                mb={0}
              >
                {business.rating_count} total reviews
              </Text>
            </HStack>
          </VStack>
        </VStack>
      </AnimatedBox>
    );
  };

  return (
    <Box>
      {/* Top 3 Podium - Only show on desktop and tablet */}
      <Box
        display={{ base: 'none', md: 'block' }}
        bg='transparent'
        mb={8}
      >
        <Flex justify='center' align='flex-end' gap={6} w='full'>
          {/* Silver - Second Place */}
          <Box
            bg={cardBg}
            borderRadius='2xl'
            textAlign='center'
            position='relative'
            cursor='pointer'
            onClick={() => top5Total[1] && handleBusinessClick(top5Total[1])}
            transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            _hover={{ 
              transform: 'translateY(-8px)', 
              shadow: '2xl',
            }}
            overflow='hidden'
            flex='1'
            maxW='400px'
            boxShadow='xl'
            borderWidth='1px'
            borderColor={borderColor}
          >
            <Box h='4px' bgGradient='linear(to-r, gray.300, gray.500, gray.300)' />
            <VStack p={6} spacing={4}>
              <Flex
                position='absolute'
                top={4}
                left={4}
                minW='36px'
                h='36px'
                px={2}
                borderRadius='full'
                bgGradient='linear(to-br, gray.300, gray.500)'
                justify='center'
                align='center'
                shadow='md'
              >
                <Text color='white' fontWeight='bold' fontSize='md' lineHeight='1'>2</Text>
              </Flex>
              
              <Icon as={FaMedal} color='gray.400' boxSize={6} />
              
              <Box p='3px' borderRadius='full' bgGradient='linear(to-br, gray.300, gray.500)'>
                <Avatar 
                  name={top5Total[1]?.name} 
                  src={businessImages[top5Total[1]?.maps_uri] || ''} 
                  bg='gray.100' 
                  boxSize='70px'
                  borderWidth='3px'
                  borderColor={cardBg}
                />
              </Box>
              
              <VStack spacing={1}>
                <Tooltip label={top5Total[1]?.name} placement='top' hasArrow openDelay={300}>
                  <Heading size='sm' noOfLines={1} color='gray.700'>{top5Total[1]?.name}</Heading>
                </Tooltip>
                <Text 
                  fontWeight='extrabold' 
                  fontSize='2xl' 
                  bgGradient='linear(to-r, gray.600, gray.800)'
                  bgClip='text'
                >
                  {top5Total[1]?.rating_count?.toLocaleString() ?? '0'}
                </Text>
                <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                  total reviews
                </Text>
              </VStack>
              
              <Box w='full' px={2}>
                <Box h='6px' bg='gray.100' borderRadius='full' overflow='hidden'>
                  <Box
                    h='full'
                    w={`${(top5Total[1]?.rating_count / (top5Total[0]?.rating_count || 1)) * 100}%`}
                    bgGradient='linear(to-r, gray.400, gray.500)'
                    borderRadius='full'
                    transition='width 0.5s ease-out'
                  />
                </Box>
              </Box>
            </VStack>
          </Box>

          {/* Gold - First Place */}
          <Box
            bg={cardBg}
            borderRadius='2xl'
            textAlign='center'
            position='relative'
            cursor='pointer'
            onClick={() => top5Total[0] && handleBusinessClick(top5Total[0])}
            transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            _hover={{ 
              transform: 'translateY(-12px) scale(1.02)', 
              shadow: '0 25px 50px -12px rgba(251, 191, 36, 0.4)',
            }}
            overflow='hidden'
            flex='1.15'
            maxW='450px'
            boxShadow='0 20px 40px -12px rgba(251, 191, 36, 0.3)'
            zIndex={2}
            mb={4}
            borderWidth='1px'
            borderColor='yellow.200'
          >
            <Box
              h='6px'
              bgGradient='linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)'
              backgroundSize='200% 100%'
              animation='shimmer 2s linear infinite'
              sx={{
                '@keyframes shimmer': {
                  '0%': { backgroundPosition: '200% 0' },
                  '100%': { backgroundPosition: '-200% 0' },
                },
              }}
            />
            <VStack p={8} spacing={4}>
              <Flex
                position='absolute'
                top={4}
                left={4}
                minW='40px'
                h='40px'
                px={2}
                borderRadius='full'
                bgGradient='linear(to-br, yellow.300, orange.400)'
                justify='center'
                align='center'
                shadow='lg'
              >
                <Text color='white' fontWeight='bold' fontSize='lg' lineHeight='1'>1</Text>
              </Flex>
              
              <Icon as={FaTrophy} color='yellow.400' boxSize={10} filter='drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))' />
              
              <Box p='4px' borderRadius='full' bgGradient='linear(to-br, yellow.300, orange.400, yellow.500)' shadow='lg'>
                <Avatar 
                  name={top5Total[0]?.name} 
                  src={businessImages[top5Total[0]?.maps_uri] || ''} 
                  bg='yellow.50' 
                  boxSize='90px'
                  borderWidth='4px'
                  borderColor={cardBg}
                />
              </Box>
              
              <VStack spacing={1}>
                <Tooltip label={top5Total[0]?.name} placement='top' hasArrow openDelay={300}>
                  <Heading size='md' noOfLines={1} color='gray.800'>{top5Total[0]?.name}</Heading>
                </Tooltip>
                <Text 
                  fontWeight='extrabold' 
                  fontSize='3xl' 
                  bgGradient='linear(to-r, yellow.500, orange.500)'
                  bgClip='text'
                >
                  {top5Total[0]?.rating_count?.toLocaleString() ?? '0'}
                </Text>
                <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                  total reviews
                </Text>
              </VStack>
              
              <Box w='full' px={2}>
                <Box h='8px' bg='yellow.50' borderRadius='full' overflow='hidden'>
                  <Box h='full' w='100%' bgGradient='linear(to-r, yellow.400, orange.400)' borderRadius='full' />
                </Box>
              </Box>
            </VStack>
          </Box>

          {/* Bronze - Third Place */}
          <Box
            bg={cardBg}
            borderRadius='2xl'
            textAlign='center'
            position='relative'
            cursor='pointer'
            onClick={() => top5Total[2] && handleBusinessClick(top5Total[2])}
            transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            _hover={{ 
              transform: 'translateY(-8px)', 
              shadow: '2xl',
            }}
            overflow='hidden'
            flex='1'
            maxW='400px'
            boxShadow='xl'
            borderWidth='1px'
            borderColor={borderColor}
          >
            <Box h='4px' bgGradient='linear(to-r, orange.200, orange.400, orange.200)' />
            <VStack p={6} spacing={4}>
              <Flex
                position='absolute'
                top={4}
                left={4}
                minW='36px'
                h='36px'
                px={2}
                borderRadius='full'
                bgGradient='linear(to-br, orange.300, orange.500)'
                justify='center'
                align='center'
                shadow='md'
              >
                <Text color='white' fontWeight='bold' fontSize='md' lineHeight='1'>3</Text>
              </Flex>
              
              <Icon as={FaAward} color='orange.400' boxSize={6} />
              
              <Box p='3px' borderRadius='full' bgGradient='linear(to-br, orange.300, orange.500)'>
                <Avatar 
                  name={top5Total[2]?.name} 
                  src={businessImages[top5Total[2]?.maps_uri] || ''} 
                  bg='orange.50' 
                  boxSize='70px'
                  borderWidth='3px'
                  borderColor={cardBg}
                />
              </Box>
              
              <VStack spacing={1}>
                <Tooltip label={top5Total[2]?.name} placement='top' hasArrow openDelay={300}>
                  <Heading size='sm' noOfLines={1} color='gray.700'>{top5Total[2]?.name}</Heading>
                </Tooltip>
                <Text 
                  fontWeight='extrabold' 
                  fontSize='2xl' 
                  bgGradient='linear(to-r, orange.500, orange.600)'
                  bgClip='text'
                >
                  {top5Total[2]?.rating_count?.toLocaleString() ?? '0'}
                </Text>
                <Text fontSize='xs' color='gray.500' fontWeight='medium' textTransform='uppercase' letterSpacing='wider'>
                  total reviews
                </Text>
              </VStack>
              
              <Box w='full' px={2}>
                <Box h='6px' bg='orange.50' borderRadius='full' overflow='hidden'>
                  <Box
                    h='full'
                    w={`${(top5Total[2]?.rating_count / (top5Total[0]?.rating_count || 1)) * 100}%`}
                    bgGradient='linear(to-r, orange.300, orange.500)'
                    borderRadius='full'
                    transition='width 0.5s ease-out'
                  />
                </Box>
              </Box>
            </VStack>
          </Box>
        </Flex>
      </Box>

      {/* Section Label */}
      {!demoMode && <SectionLabel description='Track your total review count compared to competitors. More reviews build trust and improve your local search rankings.'>
        Total Reviews
      </SectionLabel>}

      {/* Main Content Area with Tabs */}
      <Box
        bg={cardBg}
        borderRadius='lg'
        shadow='md'
        borderWidth='1px'
        borderColor={borderColor}
        mb={8}
        overflow='hidden'
      >
        <Tabs>
          <TabList
            px={4}
            pt={4}
          >
            <Tab fontWeight='medium'>Overview</Tab>
            <Tab fontWeight='medium'>Businesses</Tab>
            <Tab fontWeight='medium'>Trends</Tab>
            <Tab fontWeight='medium'>Histogram</Tab>
            <Tab fontWeight='medium'>Rankings</Tab>
            <Tab fontWeight='medium'>Details</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Panel */}
            <TabPanel>
              <SimpleGrid
                columns={{ base: 1, lg: 2 }}
                spacing={8}
                alignItems='start'
              >
                {/* Distribution Bar Chart */}
                <Box height='600px'>
                  <Heading
                    size='md'
                    mb={4}
                    color='gray.700'
                  >
                    Review Distribution
                  </Heading>
                  <ResponsiveContainer
                    width='100%'
                    height='100%'
                  >
                    <BarChart
                      data={sortedByTotal.slice(0, 20).map((business) => ({
                        name: business.name.split(' ').slice(0, 2).join(' '),
                        reviews: business.rating_count
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                      animationDuration={1000}
                      animationBegin={0}
                      animationEasing='ease-out'
                    >
                      <CartesianGrid
                        strokeDasharray='3 3'
                        stroke='#E2E8F0'
                      />
                      <XAxis
                        dataKey='name'
                        angle={-45}
                        textAnchor='end'
                        height={60}
                        interval={0}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <YAxis 
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                        domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                      />
                      <RechartsTooltip
                        formatter={(value) => value.toLocaleString()}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box
                                bg='white'
                                p={3}
                                boxShadow='lg'
                                border='1px'
                                borderColor='gray.200'
                                borderRadius='md'
                              >
                                <Text fontWeight='bold'>
                                  {payload[0].payload.name}
                                </Text>
                                <Text>
                                  {payload[0].value.toLocaleString()} reviews
                                </Text>
                                <Text color='orange.500'>
                                  Market Average:{' '}
                                  {avgReviewsPerBusiness.toLocaleString()}
                                </Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey='reviews'
                        fill={colors[0]}
                        cursor='pointer'
                        onClick={(data) => {
                          const business = sortedByTotal.find(
                            (b) =>
                              b.name.split(' ').slice(0, 2).join(' ') ===
                              data.name
                          );
                          if (business) handleBusinessClick(business);
                        }}
                        isAnimationActive={true}
                        animationDuration={1000}
                        animationBegin={300}
                        animationEasing='ease-out'
                        label={FavoriteStarBarLabel}
                      >
                        {sortedByTotal.slice(0, 20).map((business, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              index < 5 ? getColor(business.name) : '#CBD5E0'
                            }
                          />
                        ))}
                      </Bar>
                      <ReferenceLine
                        y={avgReviewsPerBusiness}
                        stroke='#ED8936'
                        strokeDasharray='5 5'
                        strokeWidth={2}
                        label={{
                          value: 'Market Average',
                          position: 'right',
                          fill: '#ED8936'
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Market Share Pie Chart */}
                <Box height='600px' overflow='visible'>
                  <Heading
                    size='md'
                    mb={4}
                    color='gray.700'
                  >
                    Market Share
                  </Heading>
                  <ResponsiveContainer
                    width='100%'
                    height='100%'
                    style={{ overflow: 'visible' }}
                  >
                    <PieChart style={{ overflow: 'visible' }}>
                      <Pie
                        data={pieChartData}
                        cx='50%'
                        cy='50%'
                        innerRadius={80}
                        outerRadius={180}
                        paddingAngle={2}
                        dataKey='value'
                        label={FavoriteStarPieLabel}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                        isAnimationActive={true}
                        animationBegin={300}
                        animationDuration={800}
                        animationEasing='ease-out'
                        cursor='pointer'
                        onClick={(data) => {
                          const business = sortedByTotal.find(
                            (b) =>
                              b.name.split(' ').slice(0, 2).join(' ') ===
                              data.name
                          );
                          if (business) handleBusinessClick(business);
                        }}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value) => value.toLocaleString()}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #E2E8F0',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Businesses Panel */}
            <TabPanel>
              <Box h={{ base: "350px", md: "600px" }}>
                <BusinessBubbleChart
                  businesses={sortedByTotal}
                  valueKey="rating_count"
                  valueLabel="Total Reviews"
                  onBusinessClick={handleBusinessClick}
                  formatValue={(val) => val?.toLocaleString() || '0'}
                  colorScheme="blue"
                />
              </Box>
            </TabPanel>

            {/* Trends Panel */}
            <TabPanel>
              <Box height='500px'>
                <Heading
                  size='md'
                  mb={4}
                  color='gray.700'
                >
                  Monthly Review Trends
                </Heading>
                <ResponsiveContainer
                  width='100%'
                  height='100%'
                >
                  <AreaChart data={monthlyTrendData}>
                    <defs>
                      {top5Total.map(
                        (business, index) =>
                          business && (
                            <linearGradient
                              key={business.name}
                              id={`color${index}`}
                              x1='0'
                              y1='0'
                              x2='0'
                              y2='1'
                            >
                              <stop
                                offset='5%'
                                stopColor={getColor(business.name)}
                                stopOpacity={0.8}
                              />
                              <stop
                                offset='95%'
                                stopColor={getColor(business.name)}
                                stopOpacity={0.1}
                              />
                            </linearGradient>
                          )
                      )}
                      <linearGradient
                        id='colorMarket'
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop
                          offset='5%'
                          stopColor='#ED8936'
                          stopOpacity={0.8}
                        />
                        <stop
                          offset='95%'
                          stopColor='#ED8936'
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray='3 3'
                      stroke='#E2E8F0'
                    />
                    <XAxis
                      dataKey='month'
                      tickFormatter={(value) => value.split('-')[1]}
                      tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                    />
                    <YAxis tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }} />
                    <RechartsTooltip
                      labelFormatter={(value) =>
                        new Date(value + '-01T00:00:00').toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long'
                        })
                      }
                      formatter={(value) => value.toLocaleString()}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Area
                      type='monotone'
                      dataKey='Market Average'
                      stroke='#ED8936'
                      strokeWidth={2}
                      fillOpacity={1}
                      fill='url(#colorMarket)'
                      strokeDasharray='5 5'
                    />
                    {top5Total.map(
                      (business, index) =>
                        business && (
                          <Area
                            key={business.name}
                            type='monotone'
                            dataKey={business.name
                              .split(' ')
                              .slice(0, 2)
                              .join(' ')}
                            stroke={getColor(business.name)}
                            strokeWidth={2}
                            fillOpacity={1}
                            fill={`url(#color${index})`}
                          />
                        )
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </TabPanel>

            {/* Improved Histogram Panel */}
            <TabPanel>
              <VStack
                spacing={8}
                align='stretch'
              >
                {/* Header Section */}
                <Box
                  textAlign='center'
                  py={4}
                >
                  <Heading
                    size='lg'
                    mb={2}
                    color='blue.600'
                  >
                    📊 Review Count Distribution Analysis
                  </Heading>
                  <Text
                    fontSize='md'
                    color='gray.600'
                    maxW='600px'
                    mx='auto'
                  >
                    Understand how businesses are distributed across different
                    review count ranges to identify market segments and
                    competitive positioning.
                  </Text>
                </Box>

                {/* Main Histogram */}
                <Box
                  bg={cardBg}
                  borderRadius='xl'
                  p={6}
                  shadow='lg'
                  borderWidth='1px'
                  borderColor={borderColor}
                >
                  <HStack
                    justify='space-between'
                    align='center'
                    mb={6}
                  >
                    <VStack
                      align='start'
                      spacing={1}
                    >
                      <Heading
                        size='md'
                        color='gray.700'
                      >
                        Review Count Distribution
                      </Heading>
                      <Text
                        fontSize='sm'
                        color='gray.500'
                      >
                        Distribution of {filteredData.length} businesses by
                        total review count ranges
                      </Text>
                    </VStack>
                    <Badge
                      colorScheme='blue'
                      fontSize='sm'
                      px={3}
                      py={1}
                    >
                      {
                        reviewCountHistogram.filter(
                          (bucket) => bucket.count > 0
                        ).length
                      }{' '}
                      Active Ranges
                    </Badge>
                  </HStack>

                  <Box
                    height='450px'
                    position='relative'
                  >
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <BarChart
                        data={reviewCountHistogram}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <defs>
                          <linearGradient
                            id='reviewGradient'
                            x1='0'
                            y1='0'
                            x2='0'
                            y2='1'
                          >
                            <stop
                              offset='5%'
                              stopColor='#2F80ED'
                              stopOpacity={0.8}
                            />
                            <stop
                              offset='95%'
                              stopColor='#2F80ED'
                              stopOpacity={0.3}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray='3 3'
                          stroke='#E2E8F0'
                        />
                        <XAxis
                          dataKey='range'
                          angle={-45}
                          textAnchor='end'
                          height={80}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          label={{
                            value: 'Review Count Ranges',
                            position: 'insideBottom',
                            offset: -5,
                            style: { textAnchor: 'middle', fill: '#4A5568' }
                          }}
                        />
                        <YAxis
                          label={{
                            value: 'Number of Businesses',
                            angle: -90,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 }
                          }}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  bg='white'
                                  p={4}
                                  boxShadow='xl'
                                  border='1px'
                                  borderColor='gray.200'
                                  borderRadius='lg'
                                  maxW='350px'
                                >
                                  <VStack
                                    align='start'
                                    spacing={2}
                                  >
                                    <Text
                                      fontWeight='bold'
                                      fontSize='md'
                                      color='blue.600'
                                    >
                                      📈 Review Range: {data.range}
                                    </Text>
                                    <Text fontSize='sm'>
                                      <strong>{data.count}</strong> businesses
                                      in this range
                                    </Text>
                                    <Text
                                      fontSize='sm'
                                      color='gray.600'
                                    >
                                      {(
                                        (data.count / filteredData.length) *
                                        100
                                      ).toFixed(1)}
                                      % of total market
                                    </Text>
                                    {data.businesses.length > 0 && (
                                      <>
                                        <Text
                                          fontSize='sm'
                                          fontWeight='medium'
                                          color='gray.700'
                                          mt={2}
                                        >
                                          Top Examples:
                                        </Text>
                                        {data.businesses
                                          .slice(0, 3)
                                          .map((business, idx) => (
                                            <Text
                                              key={idx}
                                              fontSize='xs'
                                              color='gray.600'
                                            >
                                              • {business.name} (
                                              {business.rating_count.toLocaleString()}{' '}
                                              reviews)
                                            </Text>
                                          ))}
                                        {data.businesses.length > 3 && (
                                          <Text
                                            fontSize='xs'
                                            color='blue.500'
                                            fontStyle='italic'
                                          >
                                            ... and {data.businesses.length - 3}{' '}
                                            more businesses
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar
                          dataKey='count'
                          fill='url(#reviewGradient)'
                          cursor='pointer'
                          onClick={(data) => {
                            if (
                              data &&
                              data.businesses &&
                              data.businesses.length > 0
                            ) {
                              handleBusinessClick(data.businesses[0]);
                            }
                          }}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Enhanced Statistics Grid */}
                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={6}
                >
                  <Stat
                    bg='gradient-to-br from-blue-50 to-blue-100'
                    p={5}
                    borderRadius='xl'
                    shadow='md'
                    borderWidth='1px'
                    borderColor='blue.200'
                  >
                    <StatLabel
                      fontSize='sm'
                      color='blue.600'
                      fontWeight='medium'
                    >
                      🏆 Most Common Range
                    </StatLabel>
                    <StatNumber
                      fontSize='xl'
                      color='blue.700'
                    >
                      {reviewCountHistogram.length > 0
                        ? reviewCountHistogram.reduce((max, bucket) =>
                            bucket.count > max.count ? bucket : max
                          ).range
                        : 'N/A'}
                    </StatNumber>
                    <StatHelpText color='blue.600'>
                      {reviewCountHistogram.length > 0
                        ? `${
                            reviewCountHistogram.reduce((max, bucket) =>
                              bucket.count > max.count ? bucket : max
                            ).count
                          } businesses`
                        : ''}
                    </StatHelpText>
                  </Stat>

                  <Stat
                    bg='gradient-to-br from-green-50 to-green-100'
                    p={5}
                    borderRadius='xl'
                    shadow='md'
                    borderWidth='1px'
                    borderColor='green.200'
                  >
                    <StatLabel
                      fontSize='sm'
                      color='green.600'
                      fontWeight='medium'
                    >
                      📊 Active Ranges
                    </StatLabel>
                    <StatNumber
                      fontSize='xl'
                      color='green.700'
                    >
                      {
                        reviewCountHistogram.filter(
                          (bucket) => bucket.count > 0
                        ).length
                      }
                    </StatNumber>
                    <StatHelpText color='green.600'>
                      Out of {reviewCountHistogram.length} total buckets
                    </StatHelpText>
                  </Stat>

                  <Stat
                    bg='gradient-to-br from-purple-50 to-purple-100'
                    p={5}
                    borderRadius='xl'
                    shadow='md'
                    borderWidth='1px'
                    borderColor='purple.200'
                  >
                    <StatLabel
                      fontSize='sm'
                      color='purple.600'
                      fontWeight='medium'
                    >
                      ⚖️ Average per Range
                    </StatLabel>
                    <StatNumber
                      fontSize='xl'
                      color='purple.700'
                    >
                      {reviewCountHistogram.length > 0
                        ? (
                            filteredData.length /
                            reviewCountHistogram.filter((b) => b.count > 0)
                              .length
                          ).toFixed(1)
                        : '0'}
                    </StatNumber>
                    <StatHelpText color='purple.600'>
                      Businesses per active range
                    </StatHelpText>
                  </Stat>

                  <Stat
                    bg='gradient-to-br from-orange-50 to-orange-100'
                    p={5}
                    borderRadius='xl'
                    shadow='md'
                    borderWidth='1px'
                    borderColor='orange.200'
                  >
                    <StatLabel
                      fontSize='sm'
                      color='orange.600'
                      fontWeight='medium'
                    >
                      🎯 Market Coverage
                    </StatLabel>
                    <StatNumber
                      fontSize='xl'
                      color='orange.700'
                    >
                      {(
                        (reviewCountHistogram.filter((b) => b.count > 0)
                          .length /
                          reviewCountHistogram.length) *
                        100
                      ).toFixed(0)}
                      %
                    </StatNumber>
                    <StatHelpText color='orange.600'>
                      Range utilization
                    </StatHelpText>
                  </Stat>
                </SimpleGrid>

                {/* Insights Section */}
                <Box
                  bg='gradient-to-r from-blue-50 to-indigo-50'
                  borderRadius='xl'
                  p={6}
                  borderWidth='1px'
                  borderColor='blue.200'
                >
                  <HStack
                    spacing={3}
                    mb={4}
                  >
                    <Icon
                      as={FaChartLine}
                      color='blue.500'
                      boxSize={5}
                    />
                    <Heading
                      size='md'
                      color='blue.700'
                    >
                      Distribution Insights
                    </Heading>
                  </HStack>
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={4}
                  >
                    <VStack
                      align='start'
                      spacing={2}
                    >
                      <Text
                        fontSize='sm'
                        fontWeight='medium'
                        color='blue.600'
                      >
                        Market Concentration
                      </Text>
                      <Text
                        fontSize='sm'
                        color='gray.700'
                      >
                        {reviewCountHistogram.length > 0 &&
                          (() => {
                            const maxBucket = reviewCountHistogram.reduce(
                              (max, bucket) =>
                                bucket.count > max.count ? bucket : max
                            );
                            const concentration =
                              (maxBucket.count / filteredData.length) * 100;
                            return concentration > 40
                              ? `High concentration: ${concentration.toFixed(
                                  1
                                )}% of businesses are in the ${
                                  maxBucket.range
                                } range`
                              : concentration > 25
                              ? `Moderate concentration: ${concentration.toFixed(
                                  1
                                )}% of businesses are in the ${
                                  maxBucket.range
                                } range`
                              : `Well distributed: Most common range (${
                                  maxBucket.range
                                }) contains ${concentration.toFixed(
                                  1
                                )}% of businesses`;
                          })()}
                      </Text>
                    </VStack>
                    <VStack
                      align='start'
                      spacing={2}
                    >
                      <Text
                        fontSize='sm'
                        fontWeight='medium'
                        color='blue.600'
                      >
                        Market Spread
                      </Text>
                      <Text
                        fontSize='sm'
                        color='gray.700'
                      >
                        {reviewCountHistogram.filter((b) => b.count > 0).length}{' '}
                        active ranges indicate{' '}
                        {reviewCountHistogram.filter((b) => b.count > 0)
                          .length > 6
                          ? 'high market diversity'
                          : reviewCountHistogram.filter((b) => b.count > 0)
                              .length > 3
                          ? 'moderate market diversity'
                          : 'concentrated market segments'}
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </Box>
              </VStack>
            </TabPanel>

            {/* Rankings Panel */}
            <TabPanel>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={8}
              >
                <Box>
                  <RankingListComponent
                    side='top'
                    title='Top Review Rankings'
                    data={top5Total}
                    valueKey='rating_count'
                    unit=' reviews'
                  />
                </Box>
                <Box>
                  <RankingListComponent
                    side='bottom'
                    title='Lowest Review Rankings'
                    data={sortedByTotal
                      .slice(sortedByTotal.length - 5)
                      .reverse()}
                    valueKey='rating_count'
                    unit=' reviews'
                  />
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Details Panel */}
            <TabPanel>
              <ModernDetailsTable
                data={sortedByTotal}
                columns={columns}
                onRowClick={handleRowClick}
                accentColor="blue"
                defaultSortColumnId={2}
                defaultSortAsc={false}
                searchPlaceholder="Search businesses..."
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Enhanced Histogram Section */}
      <Box
        bg={cardBg}
        borderRadius='xl'
        p={8}
        shadow='lg'
        borderWidth='1px'
        borderColor={borderColor}
        mb={8}
        position='relative'
        overflow='hidden'
      >
        {/* Background gradient accent */}
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          height='4px'
          bgGradient='linear(to-r, blue.400, purple.400, teal.400)'
        />

        <VStack
          align='stretch'
          spacing={6}
        >
          <HStack
            justify='space-between'
            align='center'
          >
            <HStack spacing={3}>
              <Icon
                as={FaChartLine}
                color='blue.500'
                boxSize={6}
              />
              <VStack
                align='start'
                spacing={1}
              >
                <Heading
                  size='md'
                  color='gray.700'
                >
                  Review Count Distribution
                </Heading>
                <Text
                  fontSize='sm'
                  color='gray.500'
                >
                  Distribution of {filteredData.length} businesses by total
                  review count ranges
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme='blue'
              variant='subtle'
              fontSize='sm'
              px={3}
              py={1}
              borderRadius='full'
            >
              {reviewCountHistogram.filter((bucket) => bucket.count > 0).length}{' '}
              Active Ranges
            </Badge>
          </HStack>

          <Box
            height='350px'
            bg={statBg}
            borderRadius='lg'
            p={4}
            position='relative'
          >
            <ResponsiveContainer
              width='100%'
              height='100%'
            >
              <BarChart
                data={reviewCountHistogram}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                barCategoryGap='20%'
              >
                <defs>
                  <linearGradient
                    id='reviewGradient'
                    x1='0'
                    y1='0'
                    x2='0'
                    y2='1'
                  >
                    <stop
                      offset='5%'
                      stopColor='#2F80ED'
                      stopOpacity={0.9}
                    />
                    <stop
                      offset='95%'
                      stopColor='#2F80ED'
                      stopOpacity={0.6}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray='3 3'
                  stroke={borderColor}
                  opacity={0.3}
                />
                <XAxis
                  dataKey='range'
                  angle={-45}
                  textAnchor='end'
                  height={60}
                  tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                />
                <YAxis
                  tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                  domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    fontSize: '14px'
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box
                          bg={cardBg}
                          p={3}
                          boxShadow='lg'
                          border='1px'
                          borderColor={borderColor}
                          borderRadius='lg'
                          maxW='300px'
                        >
                          <VStack
                            align='start'
                            spacing={1}
                          >
                            <Text
                              fontWeight='bold'
                              fontSize='sm'
                              color='blue.600'
                            >
                              📈 Range: {data.range}
                            </Text>
                            <Text fontSize='sm'>
                              <strong>{data.count}</strong> businesses
                            </Text>
                            <Text
                              fontSize='sm'
                              color='gray.600'
                            >
                              {(
                                (data.count / filteredData.length) *
                                100
                              ).toFixed(1)}
                              % of market
                            </Text>
                          </VStack>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey='count'
                  fill='url(#reviewGradient)'
                  cursor='pointer'
                  onClick={(data) => {
                    if (data && data.businesses && data.businesses.length > 0) {
                      handleBusinessClick(data.businesses[0]);
                    }
                  }}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </VStack>
      </Box>

      {/* Market Metrics */}
      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        spacing={6}
        mb={8}
      >
        <MetricCard
          icon={FaUsers}
          title='Total Market Reviews'
          value={totalMarketReviews.toLocaleString()}
          description={`Across ${sortedByTotal.length} businesses`}
          colorScheme='purple'
        />
        <MetricCard
          icon={FaChartLine}
          title='Average Reviews'
          value={avgReviewsPerBusiness.toFixed(1)}
          description='Reviews per business'
          colorScheme='blue'
        />
        <MetricCard
          icon={FaStar}
          title='Median Reviews'
          value={medianReviews.toLocaleString()}
          description='Market middle point'
          colorScheme='teal'
        />
      </SimpleGrid>

      {/* Total Reviews Over Time - All Businesses Line Chart */}
      <Box
        bg={cardBg}
        borderRadius='xl'
        p={8}
        shadow='lg'
        borderWidth='1px'
        borderColor={borderColor}
        mb={8}
        position='relative'
        overflow='hidden'
      >
        {/* Background gradient accent */}
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          height='4px'
          bgGradient='linear(to-r, blue.400, purple.500, teal.400)'
        />

        <VStack
          align='stretch'
          spacing={6}
        >
          <HStack
            justify='space-between'
            align='center'
          >
            <HStack spacing={3}>
              <Icon
                as={FaChartLine}
                color='blue.500'
                boxSize={6}
              />
              <VStack
                align='start'
                spacing={0}
              >
                <Heading
                  size='lg'
                  color='gray.700'
                >
                  Total Reviews Over Time
                </Heading>
                <Text
                  fontSize='sm'
                  color='gray.500'
                >
                  Cumulative review count for all {combinedData.length} businesses (lines only go up)
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme='purple'
              variant='subtle'
              fontSize='sm'
              px={3}
              py={1}
              borderRadius='full'
            >
              {allBusinessesLineData.length} Months
            </Badge>
          </HStack>

          <Box
            height='600px'
            bg={statBg}
            borderRadius='lg'
            p={4}
          >
            <ResponsiveContainer
              width='100%'
              height='100%'
            >
              <LineChart
                data={allBusinessesLineData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid
                  strokeDasharray='5 5'
                  stroke='#CBD5E0'
                  strokeOpacity={0.8}
                />
                <XAxis
                  dataKey='displayMonth'
                  tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                />
                <YAxis
                  tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                  label={{
                    value: 'Total Reviews',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: '#4A5568', fontSize: 12 }
                  }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                  labelFormatter={(label) => `Month: ${label}`}
                  formatter={(value, name) => [value.toLocaleString(), name]}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: '20px',
                    maxHeight: '100px',
                    overflowY: 'auto'
                  }}
                />
                {allBusinessNames.map((businessName, index) => (
                  <Line
                    key={businessName}
                    type='monotone'
                    dataKey={businessName}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Quick Insights */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={4}
          >
            <Box
              bg='blue.50'
              p={4}
              borderRadius='lg'
              borderWidth='1px'
              borderColor='blue.200'
            >
              <Text
                fontSize='sm'
                color='blue.600'
                fontWeight='medium'
              >
                📈 Tracking
              </Text>
              <Text
                fontSize='lg'
                fontWeight='bold'
                color='blue.700'
              >
                {combinedData.length} Businesses
              </Text>
              <Text
                fontSize='xs'
                color='blue.500'
              >
                Cumulative review growth
              </Text>
            </Box>
            <Box
              bg='purple.50'
              p={4}
              borderRadius='lg'
              borderWidth='1px'
              borderColor='purple.200'
            >
              <Text
                fontSize='sm'
                color='purple.600'
                fontWeight='medium'
              >
                📅 Time Period
              </Text>
              <Text
                fontSize='lg'
                fontWeight='bold'
                color='purple.700'
              >
                {allBusinessesLineData.length} Months
              </Text>
              <Text
                fontSize='xs'
                color='purple.500'
              >
                In selected date range
              </Text>
            </Box>
            <Box
              bg='teal.50'
              p={4}
              borderRadius='lg'
              borderWidth='1px'
              borderColor='teal.200'
            >
              <Text
                fontSize='sm'
                color='teal.600'
                fontWeight='medium'
              >
                ⭐ Total Reviews
              </Text>
              <Text
                fontSize='lg'
                fontWeight='bold'
                color='teal.700'
              >
                {totalMarketReviews.toLocaleString()}
              </Text>
              <Text
                fontSize='xs'
                color='teal.500'
              >
                Across all businesses
              </Text>
            </Box>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Add the Modal */}
      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={sortedBySeo}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default TotalReviewsDashboard;
